import { gql } from '@apollo/client';

import {
  MARKETING_CAMPAIGN_ALIAS_FIELDS,
  MARKETING_CHANNEL_RULE_FIELDS,
  MARKETING_COMPARE_INFO_FIELDS,
  MARKETING_COURSE_ALIAS_FIELDS,
  MARKETING_METRICS_FIELDS,
  MARKETING_OFFER_RULE_FIELDS,
  MARKETING_PAGE_METRICS_FIELDS,
  MARKETING_PIPELINE_FIELDS,
  MARKETING_SEARCH_ROW_FIELDS,
  MARKETING_SIGNAL_FIELDS,
  MARKETING_SOURCE_STATUS_FIELDS,
  MARKETING_TARGET_FIELDS,
} from '../fragments/marketing';

/**
 * ТЗ 0088 (этап 1) — чтение раздела «Маркетинг». Доступ — Admin и Marketing
 * (`MARKETING_READ_ROLES` бэкенда); без права доменный хук запрос не шлёт.
 *
 * Имена операций намеренно НЕ совпадают с полями бэкенда
 * (`marketingOverview` → `MarketingOverviewReport` и т. д.): совпадение имени
 * операции и поля схлопывает тип результата в кодгене (скилл graphql-codegen).
 *
 * Ошибки окна и параметров (`window_too_long`, `day_granularity_window`,
 * `limit_too_large`, `bad_order`…) приходят GraphQL-ошибкой с
 * `extensions.code` — перевод по коду делает доменный хук.
 */

/**
 * «Лиды и продажи»: плитки, окно сравнения, покрытие, ряд, доли каналов,
 * источники и план-факт месяцев окна.
 *
 * ТЗ 0103 §A2: `compare` — с чем сравнивать (прошлый период, год назад, свои
 * даты). Окно сравнения считает СЕРВЕР: `previous` — за него, `compareInfo`
 * — его даты и полнота; точка ряда — с корзиной сравнения того же номера
 * (`previous*`). Без `compare` `previous` — окно той же длины прямо перед,
 * `compareInfo` — null. `leadHistoryFrom` — первый день истории CRM-лидов
 * (от фильтров не зависит; `null` — лидов нет): «нет данных» вместо нулей.
 */
export const MarketingOverviewReportQuery = gql`
  query MarketingOverviewReport(
    $filter: MarketingFilter!
    $granularity: MarketingGranularity
    $compare: MarketingCompare
  ) {
    marketingOverview(
      filter: $filter
      granularity: $granularity
      compare: $compare
    ) {
      current {
        ...MarketingMetricsFields
      }
      previous {
        ...MarketingMetricsFields
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
      coverage {
        spendComplete
        missingSpendDays
        provisionalFrom
      }
      series {
        period
        dateFrom
        dateTo
        metrics {
          ...MarketingMetricsFields
        }
        previous {
          ...MarketingMetricsFields
        }
        previousPeriod
        previousDateFrom
        previousDateTo
      }
      channels {
        channel
        metrics {
          ...MarketingMetricsFields
        }
      }
      sources {
        ...MarketingSourceStatusFields
      }
      dataVersion
      planFact {
        metric
        target
        fact
        pace
      }
      leadHistoryFrom
    }
  }
  ${MARKETING_METRICS_FIELDS}
  ${MARKETING_COMPARE_INFO_FIELDS}
  ${MARKETING_SOURCE_STATUS_FIELDS}
`;

/**
 * Версия данных без показателей — сторож пересчёта после правки справочника.
 * Окно — один день: ответ дешёвый и кешируется сервером до новой версии.
 */
export const MarketingDataVersionProbeQuery = gql`
  query MarketingDataVersionProbe($filter: MarketingFilter!) {
    marketingOverview(filter: $filter) {
      dataVersion
    }
  }
`;

/**
 * Таблица разреза «Каналов» и карточки «Курсов». Фильтр авторитетен (спуск
 * сужает его), `parentKey` — только в цепочке канал → кампания → группа.
 * `series` приходит лишь при `withSeries: true` и не больше чем у 20 строк.
 * `crumbs` — имена кампаний и групп из фильтра спуска: крошки свежей ссылки
 * подписываются ими, а не id.
 */
export const MarketingBreakdownReportQuery = gql`
  query MarketingBreakdownReport(
    $filter: MarketingFilter!
    $dimension: MarketingDimension!
    $parentKey: String
    $withSeries: Boolean
    $granularity: MarketingGranularity
    $limit: Int
    $offset: Int
    $orderBy: String
    $compare: MarketingCompare
    $includeVanished: Boolean
    $having: [MarketingRowPredicate!]
    $minSpendPln: Decimal
    $minCrmLeads: Int
  ) {
    marketingBreakdown(
      filter: $filter
      dimension: $dimension
      parentKey: $parentKey
      withSeries: $withSeries
      granularity: $granularity
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      compare: $compare
      includeVanished: $includeVanished
      having: $having
      minSpendPln: $minSpendPln
      minCrmLeads: $minCrmLeads
    ) {
      dimension
      totalCount
      crumbs {
        id
        name
      }
      total {
        ...MarketingMetricsFields
      }
      predicateCounts {
        value
        count
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
      rows {
        key
        label
        platform
        status
        format
        thumbnailUrl
        salesCoverage
        metrics {
          ...MarketingMetricsFields
        }
        previous {
          ...MarketingMetricsFields
        }
        series {
          period
          dateFrom
          dateTo
          metrics {
            ...MarketingMetricsFields
          }
        }
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
  ${MARKETING_COMPARE_INFO_FIELDS}
`;

/**
 * Матрица двух измерений («Каналы»: канал × курс) — плоский список НЕПУСТЫХ
 * ячеек, отсортированный по `rowKey`, `colKey`. Ключ канала — код базы
 * (`meta`), курс без семейства — `none` (подпись пустая). При модели
 * `PARTICIPATION` ячейки — «участие» и в итог не складываются; итога у
 * матрицы нет.
 */
export const MarketingMatrixReportQuery = gql`
  query MarketingMatrixReport(
    $filter: MarketingFilter!
    $rows: MarketingDimension
    $cols: MarketingDimension
    $limit: Int
    $orderBy: String
  ) {
    marketingMatrix(
      filter: $filter
      rows: $rows
      cols: $cols
      limit: $limit
      orderBy: $orderBy
    ) {
      rowKey
      rowLabel
      colKey
      colLabel
      metrics {
        ...MarketingMetricsFields
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
`;

/**
 * Ряд окна (строка разреза — фильтр сужен до неё). ТЗ 0103: с `compare`
 * точка несёт корзину сравнения того же НОМЕРА (`previous*`) — «неделя к
 * неделе», «год назад» второй строкой.
 */
export const MarketingSeriesReportQuery = gql`
  query MarketingSeriesReport(
    $filter: MarketingFilter!
    $granularity: MarketingGranularity
    $compare: MarketingCompare
  ) {
    marketingSeries(
      filter: $filter
      granularity: $granularity
      compare: $compare
    ) {
      period
      dateFrom
      dateTo
      metrics {
        ...MarketingMetricsFields
      }
      previous {
        ...MarketingMetricsFields
      }
      previousPeriod
      previousDateFrom
      previousDateTo
    }
  }
  ${MARKETING_METRICS_FIELDS}
`;

/**
 * Продажи окна с касаниями («Качество данных»). Email у роли Marketing
 * приходит замаскированным — экран показывает как пришло.
 */
export const MarketingSalesReportQuery = gql`
  query MarketingSalesReport(
    $filter: MarketingFilter!
    $onlySales: Boolean
    $coverage: String
    $limit: Int
    $offset: Int
  ) {
    marketingSales(
      filter: $filter
      onlySales: $onlySales
      coverage: $coverage
      limit: $limit
      offset: $offset
    ) {
      totalCount
      rows {
        id
        source
        sourceId
        day
        kind
        isSale
        saleReason
        familySlug
        familyName
        pricePln
        cashPln
        emailMasked
        leadKommoId
        coverage
        lastChannel
        lastCampaign
        lastAd
        firstChannel
        leadToSaleDays
        touches {
          at
          pipelineKind
          channel
          campaign
          ad
          matchRule
          isFirst
          isLast
        }
      }
    }
  }
`;

/**
 * ТЗ 0091: одна продажа по id — для панели продажи (`insp=sale:<id>`), когда
 * её нет на загруженной странице таблицы (ссылка из журнала офлайн-конверсий,
 * пересланный адрес). Та же строка и те же поля, что у `marketingSales`, те же
 * роли и маскировка email; без окна фильтра. `null` — такой продажи нет
 * (ночная пересборка дала строкам новые id, продажу отменили).
 */
export const MarketingSaleByIdQuery = gql`
  query MarketingSaleById($id: Int!) {
    marketingSale(id: $id) {
      id
      source
      sourceId
      day
      kind
      isSale
      saleReason
      familySlug
      familyName
      pricePln
      cashPln
      emailMasked
      leadKommoId
      coverage
      lastChannel
      lastCampaign
      lastAd
      firstChannel
      leadToSaleDays
      touches {
        at
        pipelineKind
        channel
        campaign
        ad
        matchRule
        isFirst
        isLast
      }
    }
  }
`;

/**
 * «Качество данных»: покрытие, потеря меток, несопоставленное, сверка.
 * Этап 2: касания Meta всех воронок (лиды площадки — события пикселя любой
 * формы сайта, сравнивать надо с ними), входы в New leads по источнику лида
 * Kommo и признаки несопоставленной кампании (`familyMissing` —
 * «Создать правило», `campaignMissing` — «Сопоставить с кампанией»).
 * ТЗ 0103, этап 3: `siteTracking` — свой счётчик посещений: приём маячков
 * за 7 дней, отброшенные по причине, последний принятый и доля CRM-лидов
 * окна с найденным посетителем (`null` — не посчитано).
 */
export const MarketingDataQualityReportQuery = gql`
  query MarketingDataQualityReport($filter: MarketingFilter!) {
    marketingDataQuality(filter: $filter) {
      salesCoverage {
        level
        count
      }
      metaPlatformLeads
      metaCrmLeads
      metaTouchesAll
      crmLeadsBySource {
        value
        count
      }
      crmLeadsWithoutCourse
      crmLeadsTotal
      salesUnknownCourseRepeats
      unmappedSources {
        field
        value
        count
        platform
        familyMissing
        campaignMissing
      }
      unmappedCampaigns {
        field
        value
        count
        platform
        familyMissing
        campaignMissing
      }
      accountReconciliation {
        platform
        day
        campaignSum
        accountTotal
        diffPct
      }
      sources {
        ...MarketingSourceStatusFields
      }
      siteTracking {
        enabled
        collectingSince
        lastAcceptedAt
        days
        accepted
        events
        dropped {
          value
          count
        }
        queue
        leads
        linkedLeads
        linkedLeadShare
      }
    }
  }
  ${MARKETING_SOURCE_STATUS_FIELDS}
`;

/** Статус источников — «Настройки» и опрос после «Обновить сейчас». */
export const MarketingSourcesStatusQuery = gql`
  query MarketingSourcesStatus {
    marketingSources {
      ...MarketingSourceStatusFields
    }
  }
  ${MARKETING_SOURCE_STATUS_FIELDS}
`;

/**
 * ТЗ 0091 — «Настройки → Офлайн-конверсии», только чтение (Admin и
 * Marketing): флаги выгрузки, последний прогон, счётчики журнала за 7 и
 * 30 дней и последние пропуски / отказы. Коды (площадка, событие, статус,
 * причина) приходят строками — переводит экран. Мутаций отправки нет.
 */
export const MarketingOfflineConversionsReportQuery = gql`
  query MarketingOfflineConversionsReport($limit: Int) {
    marketingOfflineConversions(limit: $limit) {
      enabled
      googleEnabled
      metaEnabled
      retractEnabled
      since
      lastRun {
        status
        startedAt
        finishedAt
        errorMessage
        stats
      }
      counts7d {
        platform
        event
        status
        count
      }
      counts30d {
        platform
        event
        status
        count
      }
      issues {
        id
        platform
        event
        status
        errorCode
        eventTime
        updatedAt
        saleId
        source
        sourceId
      }
    }
  }
`;

/**
 * Справочники «Настроек» плюс варианты фильтров: юрлица (роль Marketing иначе
 * справочника юрлиц не видит) и курсы ERP для алиаса `erp_course`. Этап 2 —
 * ручные сопоставления меток CRM с кампаниями кабинета. Этап 3 — регулярка
 * бренда поиска, ключевые события GA4 (колонки «Посадочных») и хосты своего
 * сайта (страница другого хоста — не «наша»). Этап 4 — правило «сигналы →
 * задачи» и сотрудники, которых можно назначить (только те, кто видит задачи
 * ERP; только id и имя). ТЗ 0103, этап 3 — правила типов страниц сайта
 * (первое совпадение пути) и коды типов в порядке правил + `other`. SEO
 * (домены, вопросы AI, рынок DataForSEO, ТЗ 0103 этап 4) — своим запросом
 * `MarketingSeoTaxonomyReport`: он нужен только настройкам SEO.
 */
export const MarketingTaxonomyReportQuery = gql`
  query MarketingTaxonomyReport {
    marketingTaxonomy {
      channelRules {
        ...MarketingChannelRuleFields
      }
      families {
        id
        slug
        name
        isSold
        sort
        aliases {
          ...MarketingCourseAliasFields
        }
      }
      offerRules {
        ...MarketingOfferRuleFields
      }
      pipelines {
        ...MarketingPipelineFields
      }
      legalEntities {
        id
        name
      }
      courses {
        id
        name
      }
      campaignAliases {
        ...MarketingCampaignAliasFields
      }
      brandRegex
      ga4KeyEvents
      siteHosts
      signalRouting {
        kinds
        minSeverity
        assigneeIds
      }
      staffOptions {
        id
        name
      }
      pageTypeRules {
        type
        regex
      }
      pageTypes
    }
  }
  ${MARKETING_CHANNEL_RULE_FIELDS}
  ${MARKETING_COURSE_ALIAS_FIELDS}
  ${MARKETING_OFFER_RULE_FIELDS}
  ${MARKETING_PIPELINE_FIELDS}
  ${MARKETING_CAMPAIGN_ALIAS_FIELDS}
`;

/* ── Этап 2 ──────────────────────────────────────────────────────────── */

/**
 * «Креативы»: объявления окна с превью, форматом, видео-метриками и
 * выгоранием (окна выгорания — от СЕГОДНЯ, не от фильтра). `series` — только
 * при `withSeries: true` и не больше чем у 50 строк страницы; `formats` —
 * «формат × семейство × оффер» по всем строкам выдачи, до страницы.
 *
 * `adIds` — ровно эти объявления, мимо измерений фильтра, формата и
 * «только выгорающих» (окно, модель и юрлицо считают цифры): так грузится
 * выбранное объявление — ссылкой сигнала или кликом строки — вместе с его
 * рядом, не перечитывая страницу. `[]` — пусто, `null` — без отбора.
 */
export const MarketingCreativesReportQuery = gql`
  query MarketingCreativesReport(
    $filter: MarketingFilter!
    $fatigueOnly: Boolean
    $format: String
    $withSeries: Boolean
    $orderBy: String
    $limit: Int
    $offset: Int
    $adIds: [ID!]
    $compare: MarketingCompare
    $having: [MarketingRowPredicate!]
    $minSpendPln: Decimal
    $minCrmLeads: Int
    $groupBy: String
    $search: String
  ) {
    marketingCreatives(
      filter: $filter
      fatigueOnly: $fatigueOnly
      format: $format
      withSeries: $withSeries
      orderBy: $orderBy
      limit: $limit
      offset: $offset
      adIds: $adIds
      compare: $compare
      having: $having
      minSpendPln: $minSpendPln
      minCrmLeads: $minCrmLeads
      groupBy: $groupBy
      search: $search
    ) {
      totalCount
      predicateCounts {
        value
        count
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
      groups {
        key
        label
        ads
        metrics {
          ...MarketingMetricsFields
        }
      }
      rows {
        id
        platform
        externalId
        name
        level
        crmScope
        campaignId
        campaignName
        adsetName
        format
        thumbnailUrl
        title
        status
        firstDay
        ageDays
        familySlug
        offer
        metrics {
          ...MarketingMetricsFields
        }
        previous {
          ...MarketingMetricsFields
        }
        frequency
        reach28d
        hookRate
        holdRate
        cplPlatformPln
        conversionsPlatform
        fatigue {
          flagged
          reasons
          ctr7d
          ctrBase
          ctrStart
          ctrPrev
          frequency28d
          cpm7d
          cpmPrev
          cpmChangePct
          cpl7d
          cplMedian
          cplRatio
          hookRate
          hookPrev
          holdRate
          holdPrev
        }
        series {
          period
          metrics {
            ...MarketingMetricsFields
          }
        }
      }
      formats {
        format
        familySlug
        offer
        ads
        hookRate
        metrics {
          ...MarketingMetricsFields
        }
      }
    }
  }
  ${MARKETING_METRICS_FIELDS}
  ${MARKETING_COMPARE_INFO_FIELDS}
`;

/**
 * ТЗ 0103 — выбор «Объявления» панели фильтров: поиск по имени, id,
 * заголовку или кампании (`search`) среди объявлений окна, первые N по
 * расходу. Только подписи — без показателей и рядов.
 */
export const MarketingAdChoicesQuery = gql`
  query MarketingAdChoices(
    $filter: MarketingFilter!
    $search: String
    $limit: Int
  ) {
    marketingCreatives(
      filter: $filter
      search: $search
      limit: $limit
      orderBy: "-spendPln"
    ) {
      rows {
        id
        name
        platform
        campaignName
        thumbnailUrl
      }
    }
  }
`;

/**
 * Когорты недель (или месяцев) входа в New leads: продажи за 7 / 14 / 30 /
 * 60 / 90 дней. ТЗ 0103: `granularity` (WEEK | MONTH; DAY — отказ
 * `bad_granularity`), `splitBy` (channel | family | path_kind — строка на
 * когорту × значение), `pathKind` (single | multi).
 */
export const MarketingCohortsReportQuery = gql`
  query MarketingCohortsReport(
    $filter: MarketingFilter!
    $pathKind: String
    $granularity: MarketingGranularity
    $splitBy: String
  ) {
    marketingCohorts(
      filter: $filter
      pathKind: $pathKind
      granularity: $granularity
      splitBy: $splitBy
    ) {
      cohort
      dateFrom
      dateTo
      split
      splitLabel
      leads
      sales7
      sales14
      sales30
      sales60
      sales90
      revenue90Pln
      mature7
      mature14
      mature30
      mature60
      mature90
    }
  }
`;

/**
 * ТЗ 0103 §A6 — CRM-лиды по корзинам с разбивкой (`groupBy`: channel |
 * family | offer …, первые `top`, остальное — `other`) и продажи их
 * лидов-конверсий; с `compare` — корзина сравнения того же номера
 * (`previous*`).
 */
export const MarketingLeadDynamicsReportQuery = gql`
  query MarketingLeadDynamicsReport(
    $filter: MarketingFilter!
    $granularity: MarketingGranularity
    $compare: MarketingCompare
    $groupBy: String
    $top: Int
  ) {
    marketingLeadDynamics(
      filter: $filter
      granularity: $granularity
      compare: $compare
      groupBy: $groupBy
      top: $top
    ) {
      granularity
      groupBy
      groups {
        key
        label
        crmLeads
        crmLeadsRepeat
        sales
        revenuePln
      }
      points {
        period
        dateFrom
        dateTo
        crmLeads
        crmLeadsRepeat
        sales
        revenuePln
        groups {
          key
          crmLeads
          crmLeadsRepeat
          sales
          revenuePln
        }
        previousPeriod
        previousDateFrom
        previousDateTo
        previousCrmLeads
        previousCrmLeadsRepeat
        previousSales
        previousRevenuePln
      }
      totals {
        crmLeads
        crmLeadsRepeat
        sales
        revenuePln
        previous {
          crmLeads
          crmLeadsRepeat
          sales
          revenuePln
        }
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
    }
  }
  ${MARKETING_COMPARE_INFO_FIELDS}
`;

/**
 * ТЗ 0103 §A6 — многоканальные когорты: лиды и их продажи за 7…90 дней по
 * виду пути (`path_kind`: single | multi) или набору каналов.
 */
export const MarketingPathCohortsReportQuery = gql`
  query MarketingPathCohortsReport(
    $filter: MarketingFilter!
    $groupBy: String
  ) {
    marketingPathCohorts(filter: $filter, groupBy: $groupBy) {
      key
      leads
      sales7
      sales14
      sales30
      sales60
      sales90
      revenue90Pln
      conversion90
      matureShare
    }
  }
`;

/** ТЗ 0103 §A6 — «Каналы вместе»: лиды, в пути которых оба канала. */
export const MarketingChannelOverlapReportQuery = gql`
  query MarketingChannelOverlapReport($filter: MarketingFilter!) {
    marketingChannelOverlap(filter: $filter) {
      row
      col
      leads
      sales
    }
  }
`;

/**
 * ТЗ 0103 §B4 — отправленные договоры окна: только числа (ни имён, ни
 * почты). Итоги с окном сравнения, ряд, время до подписи, возраст ждущих,
 * виды договоров и покрытие атрибуции. Модель — всегда последнее касание.
 */
export const MarketingContractsReportQuery = gql`
  query MarketingContractsReport(
    $filter: MarketingFilter!
    $granularity: MarketingGranularity
    $compare: MarketingCompare
  ) {
    marketingContracts(
      filter: $filter
      granularity: $granularity
      compare: $compare
    ) {
      totals {
        sent
        opened
        signed
        pending
        lost
        signRate
        medianHoursToSign
        medianDaysLeadToContract
        previous {
          sent
          opened
          signed
          pending
          lost
          signRate
          medianHoursToSign
          medianDaysLeadToContract
        }
      }
      series {
        period
        dateFrom
        dateTo
        sent
        signed
        pending
        lost
        previous {
          sent
          signed
          pending
          lost
        }
        previousPeriod
      }
      timeToSign {
        key
        count
      }
      pendingAge {
        key
        count
      }
      byKind {
        kind
        sent
        signed
        pending
        lost
      }
      coverage {
        level
        count
      }
      unknownLegalEntity {
        sent
        signed
        pending
        lost
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
    }
  }
  ${MARKETING_COMPARE_INFO_FIELDS}
`;

/**
 * ТЗ 0103 §E6 — «Сегодня»: открытые сигналы и гипотезы бюджета по рангу
 * сервера. `params` / `metrics` — JSONString (разбор — в доменном хуке),
 * `signalId` — для «Принято / Скрыть». Без кеша сервера: решение человека
 * видно сразу. `valueKind` (lost | potential | pending | spend) и
 * `valueWindowDays` — что за деньги `valuePlnEst` и за сколько дней.
 * Финальная проверка 0103 (F-E16): `familyValuePlnEst` / `familyKeywords` —
 * оценка и число запросов курса так, как их показывает SEO › «Что
 * написать» (карточка курса «Сегодня»).
 */
export const MarketingActionsListQuery = gql`
  query MarketingActionsList($limit: Int) {
    marketingActions(limit: $limit) {
      id
      source
      kind
      severity
      titleKey
      params
      metrics
      valuePlnEst
      valueKind
      valueWindowDays
      familyValuePlnEst
      familyKeywords
      familySlug
      entityId
      entityName
      channel
      inspector
      link
      signalId
      windowFrom
      windowTo
      lastSeen
    }
  }
`;

/**
 * «Путь клиента»: косвенные воронки → New leads → покупка, покупатели внутри
 * New leads по числу косвенных касаний (модель `within_new_leads`), лаг
 * лид → продажа, скорость первой реакции.
 *
 * Лаг — `lagSales` (ПРОДАЖИ окна по корзинам дней): старое `lag` отдавало
 * продажи в поле `people` и сервером объявлено устаревшим.
 *
 * `byDevice` (29.09) — устройство заявки: CRM-лиды окна по устройству входа,
 * продажи окна по устройству лида-конверсии; всегда mobile, desktop, tablet,
 * unknown, сумма строк = итогу окна.
 */
export const MarketingJourneyReportQuery = gql`
  query MarketingJourneyReport($filter: MarketingFilter!) {
    marketingJourney(filter: $filter) {
      indirect {
        pipelineKind
        people
        reachedMain
        buyers
      }
      withinMain {
        key
        people
        buyers
      }
      lagSales {
        key
        sales
      }
      speedToLead {
        key
        people
        buyers
      }
      historyFrom
      byDevice {
        device
        crmLeads
        crmLeadsRepeat
        sales
        revenuePln
        conversion
      }
    }
  }
`;

/** План месяца: строки «канал × семейство × показатель». */
export const MarketingTargetsListQuery = gql`
  query MarketingTargetsList($month: Date!) {
    marketingTargets(month: $month) {
      ...MarketingTargetFields
    }
  }
  ${MARKETING_TARGET_FIELDS}
`;

/**
 * Лента сигналов (по умолчанию сервера — открытые и принятые). Читается без
 * кеша сервера: решение «принято / скрыть» видно сразу.
 */
export const MarketingSignalsFeedQuery = gql`
  query MarketingSignalsFeed(
    $status: [String!]
    $kinds: [String!]
    $limit: Int
  ) {
    marketingSignals(status: $status, kinds: $kinds, limit: $limit) {
      ...MarketingSignalFields
    }
  }
  ${MARKETING_SIGNAL_FIELDS}
`;

/**
 * Кампании площадки для «Сопоставить с кампанией»: имя содержит `search`,
 * первые 50 по расходу за 30 дней.
 */
export const MarketingCampaignChoicesQuery = gql`
  query MarketingCampaignChoices($platform: String!, $search: String) {
    marketingCampaignOptions(platform: $platform, search: $search) {
      id
      name
    }
  }
`;

/* ── Этап 3: поиск, сайт, спрос ──────────────────────────────────────── */

/**
 * Отчёт поисковика: ровно один `engine` — Google (GSC) или Bing, они не
 * складываются никогда. `total` — полный итог окна (единственная полная
 * цифра кликов); строки — неполный срез, `namedClicksShare` — какую долю
 * кликов итога они раскрывают (у Bing — NULL). Зоны позиций считаются по
 * строкам запросов с фильтрами бренда и семейства. У Bing строки — его
 * снимки, `snapshotDate` — когда их прочли. `includesSubdomains` — итог
 * по всему домену (ресурс GSC доменный), `otherHosts` — чужие хосты окна
 * (lms., webinars.…), которые в него вошли; у Bing — `false` и пусто.
 */
export const MarketingSearchEngineReportQuery = gql`
  query MarketingSearchEngineReport(
    $filter: MarketingFilter!
    $engine: String
    $dimension: String
    $brand: Boolean
    $zone: String
    $familySlug: String
    $search: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    marketingSearch(
      filter: $filter
      engine: $engine
      dimension: $dimension
      brand: $brand
      zone: $zone
      familySlug: $familySlug
      search: $search
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      engine
      total {
        day
        clicks
        impressions
        position
      }
      series {
        day
        clicks
        impressions
        position
      }
      namedClicksShare
      zones {
        zone
        queries
        impressions
        clicks
      }
      rows {
        ...MarketingSearchRowFields
      }
      totalCount
      provisionalFrom
      snapshotDate
      includesSubdomains
      otherHosts
    }
  }
  ${MARKETING_SEARCH_ROW_FIELDS}
`;

/**
 * Итог поисковика за ПРОШЛОЕ окно — для изменения плиток. Дни ряда нужны,
 * чтобы понять, собрано ли окно целиком: без этого «кликов стало больше»
 * могло бы значить «в прошлом окне не хватало дней».
 */
export const MarketingSearchTotalsProbeQuery = gql`
  query MarketingSearchTotalsProbe($filter: MarketingFilter!, $engine: String) {
    marketingSearch(filter: $filter, engine: $engine, limit: 1) {
      engine
      total {
        day
        clicks
        impressions
        position
      }
      series {
        day
      }
    }
  }
`;

/**
 * Трафик сайта GA4 по группе каналов (+ источник / кампания). `users` —
 * СУММА дневных пользователей: между днями не складывается в людей.
 */
export const MarketingSiteTrafficReportQuery = gql`
  query MarketingSiteTrafficReport(
    $filter: MarketingFilter!
    $groupBy: String
  ) {
    marketingTraffic(filter: $filter, groupBy: $groupBy) {
      channelGroup
      source
      medium
      campaign
      sessions
      users
      engaged
      keyEvents
    }
  }
`;

/** Переходы из AI-ассистентов: строка на «ассистент × посадочная». */
export const MarketingAiAssistantsReportQuery = gql`
  query MarketingAiAssistantsReport($filter: MarketingFilter!) {
    marketingAiReferrals(filter: $filter) {
      assistant
      landingPage
      sessions
      engaged
    }
  }
`;

/**
 * Спрос по семействам: небрендовые показы и клики GSC с прошлым окном,
 * топ-запросы, CRM-лиды с этим курсом и продажи. Семейства `isSold=false` —
 * «спрос, который мы не обслуживаем». ТЗ 0103, этап 4: спрос рынка в месяц
 * (DataForSEO, небрендовые запросы курса) и его 12 месяцев — рядом с
 * показами GSC, никогда не делится на них.
 */
export const MarketingSearchDemandReportQuery = gql`
  query MarketingSearchDemandReport($filter: MarketingFilter!) {
    marketingDemand(filter: $filter) {
      familySlug
      familyName
      isSold
      impressions
      prevImpressions
      clicks
      topQueries {
        ...MarketingSearchRowFields
      }
      crmLeads
      sales
      searchVolume
      volumeTrend
      volumeMonths
    }
  }
  ${MARKETING_SEARCH_ROW_FIELDS}
`;

/**
 * Поисковые запросы Google Ads (кампании Search): `coverageShare` — какую
 * долю расхода поисковых кампаний они раскрывают (Google прячет редкие).
 * `conversions` — цифры Google, не лиды.
 */
export const MarketingAdsSearchTermsReportQuery = gql`
  query MarketingAdsSearchTermsReport(
    $filter: MarketingFilter!
    $familySlug: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    marketingSearchTerms(
      filter: $filter
      familySlug: $familySlug
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      totalCount
      coverageShare
      rows {
        term
        campaign
        adGroup
        impressions
        clicks
        spendPln
        conversions
        familySlug
      }
    }
  }
`;

/* ── Этап 4: прогноз, недельный снимок, AI-сводка, письма ────────────── */

/**
 * Прогноз продаж месяца по семейству курса. Без `month` — текущий месяц по
 * Варшаве; прошедший месяц — факт (P10 = P50 = P90 = продажи месяца);
 * будущий — отказ `bad_month`. `smallBase` — меньше 30 лидов за 180 дней:
 * P10 / P90 не считаются (null), остаётся одна медиана. Этап лида `unknown`
 * — лид без этапа Kommo; `basis` — `stage` (вероятность этапа) или
 * `overall` (у этапа мало наблюдений — общая конверсия курса). Группы —
 * со стартом в ближайшие 60 дней; `seats` 0 — набор закрыт, null — мест
 * не задано.
 */
export const MarketingSalesForecastReportQuery = gql`
  query MarketingSalesForecastReport($month: Date, $familySlug: String) {
    marketingForecast(month: $month, familySlug: $familySlug) {
      familySlug
      familyName
      month
      salesMtd
      p10
      p50
      p90
      target
      smallBase
      openLeads {
        stage
        leads
        probability
        basis
        observations
      }
      expectedNewLeads
      leadToSale
      daysLeft
      pace28d
      expectedFromOpenLeads
      expectedFromNewLeads
      groups {
        groupId
        name
        startDate
        seats
        enrolled
        expectedBeforeStart
        fillP50
        risk
      }
    }
  }
`;

/**
 * Недельные снимки (замороженный JSON завершённой ISO-недели): только
 * `summary` — сжатые факты для полосы плиток «AI-разбора». Полный `payload`
 * экрану не нужен и сюда не выбирается.
 */
export const MarketingWeeklySnapshotsListQuery = gql`
  query MarketingWeeklySnapshotsList($limit: Int, $isoWeek: String) {
    marketingSnapshots(limit: $limit, isoWeek: $isoWeek) {
      id
      isoWeek
      dateFrom
      dateTo
      frozenAt
      dataVersion
      rebuiltCount
      summary
    }
  }
`;

/**
 * История AI-сводок (новые сверху) — без текста: список недель и статусов
 * не должен тянуть десяток длинных текстов. Статус сводки меняется без новой
 * версии данных — сервер читает его без кеша, экран опрашивает, пока есть
 * `queued` / `running`.
 */
export const MarketingDigestHistoryListQuery = gql`
  query MarketingDigestHistoryList($limit: Int) {
    marketingDigests(limit: $limit) {
      id
      isoWeek
      status
      model
      factsChecked
      rejectedNumbers
      attempts
      createdAt
      createdBy
      startedAt
      finishedAt
      error
    }
  }
`;

/**
 * Сводки ОДНОЙ недели с текстом (`text` есть только у `published`): экран
 * показывает последнюю опубликованную, даже если поверх неё уже готовится
 * или отклонена пересборка.
 */
export const MarketingDigestWeekTextQuery = gql`
  query MarketingDigestWeekText($isoWeek: String!, $limit: Int) {
    marketingDigests(isoWeek: $isoWeek, limit: $limit) {
      id
      isoWeek
      status
      text
      model
      factsChecked
      rejectedNumbers
      attempts
      createdAt
      createdBy
      startedAt
      finishedAt
      error
      inputTokens
      outputTokens
    }
  }
`;

/**
 * Письма SendGrid за окно: рассылки (single sends) со строкой в окне, ряд
 * по дням (`clicks` — уникальные клики, `impressions` — доставлено,
 * `position` всегда null), итоги рассылок, лиды и продажи с меткой письма
 * (совпадение метки, НЕ причинность). `account` и `categories` — весь
 * аккаунт, включая транзакционные письма ERP и N8N: с рассылками не
 * складываются.
 */
export const MarketingSendgridEmailReportQuery = gql`
  query MarketingSendgridEmailReport($filter: MarketingFilter!) {
    marketingEmail(filter: $filter) {
      sends {
        id
        name
        sentAt
        delivered
        uniqueOpens
        uniqueClicks
        unsubscribes
        bounces
        emailLeads
        emailSales
        categories
      }
      series {
        day
        clicks
        impressions
        position
      }
      delivered
      uniqueOpens
      uniqueClicks
      emailLeads
      emailSales
      account {
        delivered
        uniqueOpens
        uniqueClicks
        requests
      }
      categories {
        name
        delivered
        uniqueOpens
        uniqueClicks
      }
    }
  }
`;

/* ── ТЗ 0103, этап 3: «Путь клиента» — посетители и страницы ────────── */

/**
 * «Посетители» (§D6): визиты, дни и страницы до заявки корзинами,
 * «первый канал × канал визита с заявкой», типичные пути (типы страниц до
 * заявки), доля CRM-лидов окна с найденным посетителем и день старта
 * счётчика. Лиды окна — без повторов; каналы и курсы фильтра — лида.
 * `compare` — те же корзины за окно сравнения (`previous` у корзины и
 * `previous` плиток); `granularity` — только для правила «год назад».
 * Каналы — кодом базы (`seo_google`); `none` у канала заявки — лид не из
 * визита (звонок, другое устройство человека).
 */
export const MarketingVisitorJourneyReportQuery = gql`
  query MarketingVisitorJourneyReport(
    $filter: MarketingFilter!
    $compare: MarketingCompare
    $granularity: MarketingGranularity
  ) {
    marketingVisitorJourney(
      filter: $filter
      compare: $compare
      granularity: $granularity
    ) {
      kpis {
        leads
        linkedLeads
        linkedShare
        visitors
        sessions
        medianVisits
        medianDays
        medianPages
        firstVisitShare
      }
      previous {
        leads
        linkedLeads
        linkedShare
        visitors
        sessions
        medianVisits
        medianDays
        medianPages
        firstVisitShare
      }
      visitsBeforeLead {
        key
        count
        share
        previous
      }
      daysToLead {
        key
        count
        share
        previous
      }
      pagesBeforeLead {
        key
        count
        share
        previous
      }
      channelMatrix {
        firstChannel
        leadChannel
        leads
      }
      typicalPaths {
        types
        count
        share
        medianDays
      }
      collectingSince
      compareInfo {
        ...MarketingCompareInfoFields
      }
    }
  }
  ${MARKETING_COMPARE_INFO_FIELDS}
`;

/**
 * «Примеры путей»: анонимные посетители заявок окна (свежие первыми) —
 * первый визит и его канал, визитов и страниц до заявки, канал визита с
 * заявкой, курс лида, исход. Ни e-mail, ни телефона; `id` — для таймлайна.
 */
export const MarketingVisitorSamplesListQuery = gql`
  query MarketingVisitorSamplesList(
    $filter: MarketingFilter!
    $limit: Int
    $offset: Int
    $onlyLinked: Boolean
  ) {
    marketingVisitors(
      filter: $filter
      limit: $limit
      offset: $offset
      onlyLinked: $onlyLinked
    ) {
      totalCount
      roundedToDay
      rows {
        id
        firstSeenAt
        firstChannel
        sessions
        pageviews
        pages
        leadAt
        leadChannel
        daysToLead
        familySlug
        outcome
      }
    }
  }
`;

/**
 * Таймлайн анонимного посетителя (панель справа `insp=visitor:<id>`):
 * визиты → страницы → лид → договор → подпись → продажа. Неизвестный id —
 * `null`. От окна не зависит.
 */
export const MarketingVisitorTimelineCardQuery = gql`
  query MarketingVisitorTimelineCard($id: ID!) {
    marketingVisitor(id: $id) {
      id
      roundedToDay
      firstSeenAt
      lastSeenAt
      sessions
      pageviews
      devices
      device
      events {
        kind
        at
        channel
        subChannel
        landingPath
        pages
        pageviews
        durationS
        formSubmits
        device
        familySlug
        isRepeat
        pageType
        outcome
      }
    }
  }
`;

/**
 * «Страницы» (§C3): GA4, GSC, свой счётчик, CRM-лиды (посадочная / форма /
 * видели до заявки), продажи и конверсии по странице. Каналы фильтра сужают
 * свой счётчик и лиды (GA4 и GSC тогда null); кампании, предложения и
 * модель на страницы не действуют. `typeCounts` — строки по типам ДО
 * фильтра типов (числа чипов групп), `denominator` — знаменатель окна.
 * Сортировка — показатель или `delta_<показатель>` (только с `compare`).
 */
export const MarketingSitePagesReportQuery = gql`
  query MarketingSitePagesReport(
    $filter: MarketingFilter!
    $compare: MarketingCompare
    $granularity: MarketingGranularity
    $pageTypes: [String!]
    $familySlug: String
    $search: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    marketingPages(
      filter: $filter
      compare: $compare
      granularity: $granularity
      pageTypes: $pageTypes
      familySlug: $familySlug
      search: $search
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      totalCount
      denominator
      collectingSince
      typeCounts {
        value
        count
      }
      totals {
        ...MarketingPageMetricsFields
      }
      previousTotals {
        ...MarketingPageMetricsFields
      }
      rows {
        path
        pageType
        familySlug
        firstSeen
        metrics {
          ...MarketingPageMetricsFields
        }
        previous {
          ...MarketingPageMetricsFields
        }
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
    }
  }
  ${MARKETING_PAGE_METRICS_FIELDS}
  ${MARKETING_COMPARE_INFO_FIELDS}
`;

/**
 * Карточка страницы (панель справа `insp=page:<путь>`): ряд со сравнением
 * по корзинам шага, откуда входят (свой счётчик — канал визита, GA4 —
 * группа каналов), следующие страницы (`path: null` — выход с сайта) и
 * запросы Search Console страницы.
 */
export const MarketingSitePageCardQuery = gql`
  query MarketingSitePageCard(
    $path: String!
    $filter: MarketingFilter!
    $compare: MarketingCompare
    $granularity: MarketingGranularity
  ) {
    marketingPage(
      path: $path
      filter: $filter
      compare: $compare
      granularity: $granularity
    ) {
      path
      pageType
      familySlug
      firstSeen
      known
      denominator
      collectingSince
      metrics {
        ...MarketingPageMetricsFields
      }
      previous {
        ...MarketingPageMetricsFields
      }
      series {
        period
        dateFrom
        dateTo
        previousPeriod
        previousDateFrom
        previousDateTo
        metrics {
          ...MarketingPageMetricsFields
        }
        previous {
          ...MarketingPageMetricsFields
        }
      }
      entrySources {
        source
        key
        sessions
        crmLeads
        keyEvents
      }
      nextPages {
        path
        pageType
        count
        share
      }
      gscQueries {
        query
        clicks
        impressions
        position
      }
      compareInfo {
        ...MarketingCompareInfoFields
      }
    }
  }
  ${MARKETING_PAGE_METRICS_FIELDS}
  ${MARKETING_COMPARE_INFO_FIELDS}
`;
